import { useMemo } from "react";
import { Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { fareExplanationEn } from "@/locales/fareExplanation.en";
import { getCurrencySymbol } from "@/utils/currency";
import type {
  FareCalculationResult,
  FareExplanationData,
} from "@/types/fareCalculation";

type FactorImpact = "High" | "Medium" | "Low";

type ExplanationFactor = {
  factorKey: string;
  descriptionKey?: string;
  descriptionTemplateKey?: string;
  descriptionVars?: Record<string, string | number>;
  value: string;
  impact: FactorImpact;
};

interface FareCalculationExplanationProps {
  open: boolean;
  onClose: () => void;
  fareData: FareExplanationData;
  fareCalculationData: FareCalculationResult | null;
}

function translate(
  key: string,
  vars?: Record<string, string | number>
): string {
  let text = fareExplanationEn[key] ?? key;
  if (vars) {
    Object.entries(vars).forEach(([name, value]) => {
      text = text.replace(
        new RegExp(`\\{\\{${name}\\}\\}|\\{${name}\\}`, "g"),
        String(value)
      );
    });
  }
  return text;
}

const impactStyles: Record<FactorImpact, string> = {
  High: "bg-red-100 text-red-700",
  Medium: "bg-amber-100 text-amber-700",
  Low: "bg-green-100 text-green-700",
};

export function FareCalculationExplanation({
  open,
  onClose,
  fareData,
  fareCalculationData,
}: FareCalculationExplanationProps) {
  const currencySymbol = getCurrencySymbol(fareData.currency);

  const explanationFactors = useMemo((): ExplanationFactor[] => {
    const { make, model } = fareData.vehicleDetails;

    if (fareCalculationData) {
      const p = fareCalculationData.pricingBreakdown;
      return [
        {
          factorKey: "Base Fare",
          descriptionKey:
            "Calculated based on distance between origin and destination",
          value: `${currencySymbol} ${p.baseDistanceRate.toFixed(2)}`,
          impact: "High",
        },
        {
          factorKey: "Fuel Cost Share",
          descriptionKey: "Share of fuel costs for the trip",
          value: `${currencySymbol} ${p.fuelCostShare.toFixed(2)}`,
          impact: "Medium",
        },
        {
          factorKey: "Toll Cost Share",
          descriptionKey: "Share of toll costs for the route",
          value: `${currencySymbol} ${p.tollCostShare.toFixed(2)}`,
          impact: "Medium",
        },
        {
          factorKey: "Driver Service Fee",
          descriptionKey: "Fee for driver services",
          value: `${currencySymbol} ${p.driverServiceFee.toFixed(2)}`,
          impact: "Low",
        },
        {
          factorKey: "Car Premium",
          descriptionTemplateKey: "Premium for {make} {model}",
          descriptionVars: { make, model },
          value: `${currencySymbol} ${p.carPremium.toFixed(2)}`,
          impact: "Medium",
        },
        {
          factorKey: "Feature Premium",
          descriptionKey: "Additional amenities premium",
          value: `${currencySymbol} ${p.featurePremium.toFixed(2)}`,
          impact: "Low",
        },
        {
          factorKey: "Timing Adjustment",
          descriptionKey: "Adjustment based on time of day",
          value: `${currencySymbol} ${p.timingAdjustment.toFixed(2)}`,
          impact: "Medium",
        },
      ];
    }

    return [
      {
        factorKey: "Base Fare",
        descriptionKey:
          "Calculated based on distance between origin and destination",
        value: `${currencySymbol} ${Math.round(fareData.suggestedFare * 0.4)}`,
        impact: "High",
      },
      {
        factorKey: "Vehicle Type",
        descriptionTemplateKey: "Premium factor for {make} {model}",
        descriptionVars: { make, model },
        value: `${currencySymbol} ${Math.round(fareData.suggestedFare * 0.15)}`,
        impact: "Medium",
      },
      {
        factorKey: "Comfort Features",
        descriptionKey:
          "Additional amenities like AC, Wi-Fi, Premium Sound System",
        value: `${currencySymbol} ${Math.round(fareData.suggestedFare * 0.2)}`,
        impact: "Medium",
      },
      {
        factorKey: "Time of Day",
        descriptionKey: "Peak hour pricing factor",
        value: `${currencySymbol} ${Math.round(fareData.suggestedFare * 0.1)}`,
        impact: "Low",
      },
      {
        factorKey: "Demand Factor",
        descriptionKey: "Current demand for rides on this route",
        value: `${currencySymbol} ${Math.round(fareData.suggestedFare * 0.15)}`,
        impact: "Medium",
      },
    ];
  }, [fareCalculationData, fareData, currencySymbol]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="z-[200] max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {translate("Fare Calculation Explained")}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {translate(
            "Our AI analyzes multiple factors to suggest a fair price for your ride"
          )}
        </p>

        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-center justify-between py-1 text-sm">
            <span className="text-muted-foreground">
              {translate("Your Selected Fare")}
            </span>
            <span className="font-semibold">
              {currencySymbol} {fareData.selectedFare}
            </span>
          </div>
          <div className="flex items-center justify-between py-1 text-sm">
            <span className="text-muted-foreground">
              {translate("Suggested by AI")}
            </span>
            <span className="font-semibold text-primary">
              {currencySymbol} {fareData.suggestedFare}
            </span>
          </div>
          <div className="flex items-center justify-between py-1 text-sm">
            <span className="text-muted-foreground">
              {translate("Fair Range")}
            </span>
            <span className="font-semibold">
              {currencySymbol} {fareData.minFare} - {currencySymbol}{" "}
              {fareData.maxFare}
            </span>
          </div>
          {fareCalculationData?.perSeatPricing?.totalKilometers != null && (
            <div className="flex items-center justify-between py-1 text-sm">
              <span className="text-muted-foreground">
                {translate("Total Distance")}
              </span>
              <span className="font-semibold">
                {fareCalculationData.perSeatPricing.totalKilometers.toFixed(1)}{" "}
                km
              </span>
            </div>
          )}
        </div>

        <h3 className="text-base font-bold">
          {translate("How We Calculate Your Fare")}
        </h3>

        <div className="space-y-3">
          {explanationFactors.map((factor, index) => (
            <div
              key={index}
              className="rounded-xl border border-border bg-muted/40 p-4"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">
                  {translate(factor.factorKey)}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${impactStyles[factor.impact]}`}
                >
                  {translate(factor.impact)}
                </span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {factor.descriptionTemplateKey
                  ? translate(
                      factor.descriptionTemplateKey,
                      factor.descriptionVars
                    )
                  : translate(factor.descriptionKey ?? "")}
              </p>
              <p className="mt-2 text-right text-sm font-semibold text-primary">
                {factor.value}
              </p>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="mb-2 flex items-center gap-2">
            <Info className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-700">
              {translate("AI Pricing Intelligence")}
            </span>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {translate(
              "Our machine learning algorithms analyze real-time data including traffic patterns, historical pricing, vehicle features, and rider preferences to ensure fair and competitive pricing."
            )}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
